import { Alert, Paper, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import Button from "@mui/material/Button";
import axios from "axios";
import * as React from "react";
import { LoadingWrapper } from "../../components/LoadingWrapper";
import { Config } from "../../config/config";
import { Helpers } from "../../utils/helpers";
import { PopupMessage } from "../../utils/message";

interface FeedbackStates {
    loaded: boolean;
    processes: any[];
    environment: {
        buildings: any[];
    };
}

interface FeedbackProps {
    playerId: string;
    activeEnv: any;
    playerData: any;
    heroesConfig: any;
    classes: any;
}

export class PlayerProcesses extends React.Component<FeedbackProps, FeedbackStates> {

    constructor(props) {
        super(props);

        this.state = {
            loaded: false,
            processes: [],
            environment: {
                buildings: []
            }
        };
    }

    componentDidMount() {
        this.fetchData();
    }

    fetchProcesses = () => axios.get(`${Config.getInstance.apiUrl}/api/user/${this.props.playerId}/processes`, {
        headers: {Authorization: Helpers.getSessionId(), env: this.props.activeEnv},
    });

    fetchData = () =>
        axios.all([this.fetchProcesses()]).then(axios.spread((processes) => {
            this.setState({
                loaded: true,
                processes: processes.data.data,
                environment: processes.data.environment
            });
        })).catch((_err) => {
            PopupMessage.error("Something went wrong");
        });

    refreshList = () => {
        this.setState({loaded: false}, () => {
            this.fetchData();
        });
    };

    finishProcess = async (process: any) => {
        if (!confirm("Are you sure you want to finish this player process?\n\nThe process finish date will be set to current time so that the next the player will go online, the process will finish instantly")) {
            return;
        }

        try {
            await axios.post(Config.getInstance.apiUrl + "/api/user/" + this.props.playerId + "/processes/" + process.id + "/finish", {}, {
                headers: {Authorization: Helpers.getSessionId(), env: this.props.activeEnv},
            });

            const processes = [...this.state.processes];

            const index = processes.findIndex(_process => _process.id === process.id);

            processes[index].finished_at = new Date().toISOString();

            this.setState({
                processes
            });

            PopupMessage.success("Process successfully finished");
        } catch (e) {
            PopupMessage.error("Something went wrong");
        }
    };

    resetProcess = async (process: any) => {
        if (!confirm("Are you sure you want to reset this player process?\n\nThe process confirmation date will be removed, which will trigger the process action again for the next play session")) {
            return;
        }

        try {
            await axios.post(Config.getInstance.apiUrl + "/api/user/" + this.props.playerId + "/processes/" + process.id + "/reset", {}, {
                headers: {Authorization: Helpers.getSessionId(), env: this.props.activeEnv},
            });

            const processes = [...this.state.processes];

            const index = processes.findIndex(_process => _process.id === process.id);

            processes[index].confirmed_at = null;
            processes[index].seen_at = null;

            this.setState({
                processes
            });

            PopupMessage.success("Process successfully finished");
        } catch (e) {
            PopupMessage.error("Something went wrong");
        }
    };

    getObjectId(process: any) {
        if (process.type === "CREATE" || process.type === "UPGRADE") {
            const [, index] = atob(process.object_id).split(":");

            const object = (this?.state?.environment?.buildings || [])
                .find(_object => _object.index === +index);

            if (!object) {
                return process.object_id;
            }

            return `${this.getBuildingName(object.id)} - ${object.id} (${process.object_id})`;
        }

        if (process.type === "RESEARCH") {
            return `${this.getHeroName(process.object_id)} - ${process.object_id}`;
        }

        return process.object_id;
    }

    getBuildingName(elementId) {
        return this.getTranslation(`building_${elementId}name`);
    }

    getHeroName(heroId) {
        return this.getTranslation(`char_${heroId}name`);
    }

    getTranslation(key: string) {
        const translation = JSON.parse(localStorage.getItem("LOCALIZATION")).find(item => item.key === key);

        if (!translation) {
            return key;
        }

        return translation.value;
    }

    renderRows() {
        if (!this.state.loaded) {
            return <TableRow key="loading-row">
                <TableCell colSpan={9}>
                    {Helpers.renderSkeletonRows(3, 50)}
                </TableCell>
            </TableRow>;
        }

        if (this.state.processes.length === 0) {
            return <TableRow key="records_not_found">
                <TableCell colSpan={9}>
                    <Alert severity="info">No active processes</Alert>
                </TableCell>
            </TableRow>;
        }

        return this.state.processes.map(process => (
            <TableRow key={process.id}>
                <TableCell>{process.id}</TableCell>
                <TableCell>{this.getObjectId(process)}</TableCell>
                <TableCell>{process.type}</TableCell>
                <TableCell>{process.after_action_value}</TableCell>
                <TableCell>{Helpers.formatDateWithTime(process.created_at)}</TableCell>
                <TableCell>{Helpers.formatDateWithTime(process.finished_at)}</TableCell>

                <TableCell>{process.confirmed_at ? Helpers.formatDateWithTime(process.confirmed_at) : "-"}</TableCell>
                <TableCell>{process.seen_at ? Helpers.formatDateWithTime(process.seen_at) : "-"}</TableCell>

                <TableCell>
                    {!process.confirmed_at &&
                    <Button disableElevation variant="contained" color="primary"
                            onClick={() => this.finishProcess(process)}
                            style={{marginRight: 10}}>
                        Finish
                    </Button>}

                    {process.confirmed_at &&
                    <Button disableElevation variant="contained" color="secondary"
                            onClick={() => this.resetProcess(process)}>
                        Reset
                    </Button>}
                </TableCell>
            </TableRow>
        ));
    }

    render() {
        return (
            <Paper elevation={0}>
                <LoadingWrapper loading={!this.state.loaded}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Process ID</TableCell>
                                <TableCell>Object ID</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Process result</TableCell>
                                <TableCell>Start date</TableCell>
                                <TableCell>End date</TableCell>
                                <TableCell>Confirmed At</TableCell>
                                <TableCell>Seen At</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {this.renderRows()}
                        </TableBody>
                    </Table>
                </LoadingWrapper>
            </Paper>
        );
    }
}
